/**
 * Food collection list provider.
 *
 * Contracts:
 * - `DataProvider` provides route-level ids (`userId`, `scopeId`).
 * - List views should use `useCollectionPermissions()` which returns a per-row permission function
 *   (combines AccessGrant checks + domain constraints like `!isDefault`).
 * - Mutations are thin `z.mutate(...)` wrappers; UIs own validation and orchestration.
 */
import {
  createContext,
  useContext as useReactContext,
  useMemo,
  type PropsWithChildren,
} from "react";
import { useQuery, useZero } from "@corpuscomplete/zero-data-transformer/react";
import type { Zero, QueryRowType, PromiseWithServerResult } from "@rocicorp/zero";
import { mutators, queries } from "@corpuscomplete/core/zero";
import { AccessGrant } from "@corpuscomplete/core/auth/grant";
import { FoodCollection } from "@corpuscomplete/core/nutrition/food-collection";

type RootManagementRow = QueryRowType<typeof queries.foodCollection.forRootManagement>;

export namespace FoodCollectionList {
  export type Context = {
    scopeId: string;
    userId: string;
  };

  const DataContext = createContext<Context | null>(null);

  export type DataProviderProps = PropsWithChildren<{
    scopeId: string;
    userId: string;
  }>;

  export function DataProvider({ children, scopeId, userId }: DataProviderProps) {
    return <DataContext value={{ scopeId, userId }}>{children}</DataContext>;
  }

  export function useContext(): Context {
    const context = useReactContext(DataContext);
    if (!context) {
      throw new Error(
        "FoodCollectionList.useContext must be used within FoodCollectionList.DataProvider",
      );
    }
    return context;
  }

  export type CollectionPreview = RootManagementRow & {
    role: AccessGrant.Role;
    isDefault: boolean;
    foodCount: number;
  };

  export function useCollections(): CollectionPreview[] {
    const { scopeId } = useContext();
    const [collections] = useQuery(queries.foodCollection.forRootManagement());

    return useMemo(
      () =>
        collections
          .filter((collection) => collection.scopeId === scopeId)
          .map((c) => ({
            ...c,
            role: AccessGrant.highestRole(c.grants),
            isDefault: c.defaultForUserId !== null,
            foodCount: c.foodMetas.length,
          })),
      [collections, scopeId],
    );
  }

  export function useDefaultCollection(): CollectionPreview | undefined {
    const collections = useCollections();
    return useMemo(() => collections.find((c) => c.isDefault), [collections]);
  }

  export function useLoadingState(): { isLoading: boolean } {
    const [, details] = useQuery(queries.foodCollection.forRootManagement());
    return {
      isLoading: details.type === "unknown",
    };
  }

  export type Mutations = {
    create: (params: { id: string; name: string }) => PromiseWithServerResult;
    rename: (id: string, name: string) => PromiseWithServerResult;
    del: (id: string) => PromiseWithServerResult;
  };

  export function useMutations(): Mutations {
    const z = useZero();
    const { scopeId } = useContext();

    return useMemo(
      () => ({
        create: ({ id, name }) => z.mutate(mutators.foodCollection.create({ id, scopeId, name })),
        rename: (id, name) => z.mutate(mutators.foodCollection.rename({ id, name })),
        del: (id) => z.mutate(mutators.foodCollection.delete({ id })),
      }),
      [scopeId, z],
    );
  }

  export function preload(zero: Zero): void {
    void zero.run(queries.foodCollection.forRootManagement());
  }

  export type CollectionPermissions = {
    canRename: boolean;
    canDelete: boolean;
  };

  export function useCollectionPermissions(): (
    collection: CollectionPreview,
  ) => CollectionPermissions {
    const [grants] = useQuery(queries.accessGrant.myGrants());

    return useMemo(() => {
      return (collection: CollectionPreview) => {
        const perms = AccessGrant.checkPermissions(
          grants,
          FoodCollection.Permissions,
          collection.id,
        );
        return {
          canRename: perms.canRename && !collection.isDefault,
          canDelete: perms.canDelete && !collection.isDefault && collection.foodCount === 0,
        };
      };
    }, [grants]);
  }
}
